import React, { useState, useEffect } from 'react';
import api from '../api/axios';

interface DriveFolder {
    id: string;
    name: string;
}

interface SharedDrive {
    id: string;
    name: string;
}

interface BreadcrumbItem {
    id: string;
    name: string;
    driveId?: string;
}

interface DriveFolderSelectorProps {
    selectedFolderId: string;
    onFolderSelect: (folderId: string, folderName: string) => void;
}

export default function DriveFolderSelector({ selectedFolderId, onFolderSelect }: DriveFolderSelectorProps) {
    const [sharedDrives, setSharedDrives] = useState<SharedDrive[]>([]);
    const [folders, setFolders] = useState<DriveFolder[]>([]);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
    const [currentDriveId, setCurrentDriveId] = useState<string | undefined>();
    const [activeTab, setActiveTab] = useState<string>('myDrive');
    const [loading, setLoading] = useState(false);
    const [drivesLoading, setDrivesLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedFolderName, setSelectedFolderName] = useState('');

    useEffect(() => {
        loadSharedDrives();
        loadFolders();
    }, []);

    const loadSharedDrives = async () => {
        setDrivesLoading(true);
        try {
            const res = await api.get('/drive/shared-drives');
            if (res.data.success) {
                setSharedDrives(res.data.drives || []);
            }
        } catch {
            // silently fail — shared drives unavailable
        } finally {
            setDrivesLoading(false);
        }
    };

    const loadFolders = async (parentId?: string, driveId?: string) => {
        setLoading(true);
        setError('');
        try {
            const params: Record<string, string> = {};
            if (parentId) params.parentId = parentId;
            if (driveId) params.driveId = driveId;

            const query = new URLSearchParams(params).toString();
            const res = await api.get(`/drive/folders${query ? '?' + query : ''}`);

            if (res.data.success) {
                setFolders(res.data.folders || []);
            } else {
                setError(res.data.error || 'Failed to load folders');
            }
        } catch {
            setError('Failed to connect to drive service');
        } finally {
            setLoading(false);
        }
    };

    const handleTabSelect = (tabId: string) => {
        setActiveTab(tabId);
        setBreadcrumb([]);
        if (tabId === 'myDrive') {
            setCurrentDriveId(undefined);
            loadFolders();
        } else {
            const drive = sharedDrives.find(d => d.id === tabId);
            if (drive) {
                setCurrentDriveId(drive.id);
                loadFolders(drive.id, drive.id);
            }
        }
    };

    const handleEnterFolder = (folder: DriveFolder) => {
        setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name, driveId: currentDriveId }]);
        loadFolders(folder.id, currentDriveId);
    };

    const handleBreadcrumbClick = (index: number) => {
        const item = breadcrumb[index];
        setBreadcrumb(prev => prev.slice(0, index + 1));
        if (index === 0 && activeTab !== 'myDrive') {
            loadFolders(item.driveId, item.driveId);
        } else {
            loadFolders(item.id, item.driveId);
        }
    };

    const handleRootClick = () => {
        setBreadcrumb([]);
        if (activeTab === 'myDrive') {
            loadFolders();
        } else {
            loadFolders(activeTab, activeTab);
        }
    };

    const handleSelectFolder = (folder: DriveFolder) => {
        onFolderSelect(folder.id, folder.name);
        setSelectedFolderName(folder.name);
    };

    const handleClear = () => {
        onFolderSelect('', '');
        setSelectedFolderName('');
    };

    return (
        <div className="drive-folder-selector">
            <label className="course-form-label">Google Drive Folder (Optional)</label>

            {selectedFolderId && (
                <div className="drive-selected-banner">
                    <span>Selected: <strong>{selectedFolderName || selectedFolderId}</strong></span>
                    <button type="button" className="drive-clear-btn" onClick={handleClear}>Clear</button>
                </div>
            )}

            {/* Drive tabs */}
            <div className="drive-tabs">
                <button
                    type="button"
                    className={`drive-tab${activeTab === 'myDrive' ? ' drive-tab-active' : ''}`}
                    onClick={() => handleTabSelect('myDrive')}
                >
                    My Drive
                </button>
                {!drivesLoading && sharedDrives.map(drive => (
                    <button
                        key={drive.id}
                        type="button"
                        className={`drive-tab${activeTab === drive.id ? ' drive-tab-active' : ''}`}
                        onClick={() => handleTabSelect(drive.id)}
                    >
                        {drive.name}
                    </button>
                ))}
            </div>

            {/* Breadcrumb */}
            <div className="drive-breadcrumb">
                <button type="button" className="drive-breadcrumb-item" onClick={handleRootClick}>
                    {activeTab === 'myDrive' ? 'My Drive' : (sharedDrives.find(d => d.id === activeTab)?.name ?? 'Drive')}
                </button>
                {breadcrumb.map((item, i) => (
                    <React.Fragment key={item.id}>
                        <span className="drive-breadcrumb-sep">/</span>
                        <button
                            type="button"
                            className="drive-breadcrumb-item"
                            onClick={() => handleBreadcrumbClick(i)}
                        >
                            {item.name}
                        </button>
                    </React.Fragment>
                ))}
            </div>

            {/* Folder list */}
            <div className="drive-folder-list">
                {loading ? (
                    <div className="drive-folder-loading">Loading folders...</div>
                ) : error ? (
                    <div className="drive-folder-error">{error}</div>
                ) : folders.length === 0 ? (
                    <div className="drive-folder-empty">No subfolders found</div>
                ) : (
                    folders.map(folder => (
                        <div
                            key={folder.id}
                            className={`drive-folder-row${selectedFolderId === folder.id ? ' drive-folder-row-selected' : ''}`}
                        >
                            <button
                                type="button"
                                className="drive-folder-name-btn"
                                onClick={() => handleEnterFolder(folder)}
                                title="Browse into folder"
                            >
                                <span className="drive-folder-icon">📁</span>
                                {folder.name}
                            </button>
                            <button
                                type="button"
                                className={`drive-select-btn${selectedFolderId === folder.id ? ' drive-select-btn-active' : ''}`}
                                onClick={() => handleSelectFolder(folder)}
                            >
                                {selectedFolderId === folder.id ? '✓ Selected' : 'Select'}
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
